import { COOKIES } from '@/constants';
import analyticsEvents from '@/utils/analyticsEvents';
import { dataLayerTrack } from '@/utils/datalayer';
import { eventTracker, reset } from '@/utils/eventTracker';
import { getPageTypeFromRouteName } from '@/utils/getPageTypeFromRouteName';
import gtmEvents from '@/utils/gtmEvents';
import { getBasicUserData } from '@/utils/gtmEventTracker';
import { touchUserSession } from '@/utils/sessionManager';
import { HttpClient } from '@sentry/integrations';
import { BrowserTracing } from '@sentry/tracing';
import * as Sentry from '@sentry/vue';
import Cookies from 'js-cookie';
import Vue from 'vue';
import VueMeta from 'vue-meta';
import VueMixpanel from 'vue-mixpanel';
import Vuex from 'vuex';
import App from './App.vue';
import zendesk from './plugins/zendesk';
import router from './router';
import { purgeOldCacheKeys } from './services/local-storage';
import GeneralStore from './store';
import BrowseStore from './store/browse';
import SellerStore from './store/browse/seller';
import SearchStore from './store/search';
import SubscriptionStore from './store/subscription';
import TeamStore from './store/team';
import TeamDetailsStore from './store/team-details';
import { getUTCDateTime } from './utils/date';
import { setRouteSuperProperties } from './utils/eventSuperProperties';

purgeOldCacheKeys();

Vue.use(Vuex);
Vue.use(VueMeta);

Vue.use(VueMixpanel, {
  token: process.env.MIX_MIXPANEL_PROJECT_TOKEN,
  config: {
    persistence: 'localStorage',
  },
});

Vue.directive('event-track', {
  bind: (el, binding) => {
    el.addEventListener('click', () => {
      const modifiers = Object.keys(binding.modifiers);
      switch (modifiers.length) {
        case 0:
          eventTracker(analyticsEvents[binding.arg], binding.value);
          break;
        case 1:
          eventTracker(analyticsEvents[binding.arg][modifiers[0]], binding.value);
          break;
        default:
          console.error("The 'event-track' directive doesn't support more than one modifier");
      }
    });
  },
});

Vue.directive('intersection', {
  inserted(el, binding) {
    const options = {
      rootMargin: '0px',
      threshold: 0.9,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Fire the event when the component enters the viewport
          binding.value();
          // Disconnect the observer once the event is fired
          observer.disconnect();
        }
      });
    }, options);

    observer.observe(el);
  },
});
const store = new Vuex.Store({
  ...GeneralStore,
  modules: {
    BrowseStore,
    SellerStore,
    search: SearchStore,
    subscription: SubscriptionStore,
    team: TeamStore,
    teamDetails: TeamDetailsStore,
  },
});

if (typeof window !== 'undefined') {
  if (process.env.MIX_APP_ENV === 'production' && window.location.hostname === 'motionarray.com') {
    Sentry.init({
      Vue,
      dsn: 'https://d6ea124a3c5444c6aa843619b2064c8d@o4504270633959424.ingest.sentry.io/4504824921718784',
      integrations: [
        new BrowserTracing({
          routingInstrumentation: Sentry.vueRouterInstrumentation(router),
          tracePropagationTargets: ['motionarray.com', /^\//],
        }),
        new HttpClient({
          failedRequestStatusCodes: [500, 403],
        }),
        new Sentry.Replay(),
      ],
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.5,
      // This sets the sample rate to be 10%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: 0.1,

      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: 0.1,
    });
  }
}
if (typeof window !== 'undefined' && process.env.MIX_APP_ENV === 'production') {
  zendesk(store);
}

const trackPageMetaDataEvent = (to) => {
  const pageTitle = to.matched[0].instances.default?.$metaInfo?.title || to.matched[0].name || '';
  const gtmData = {
    pageTitle,
    page_environment: process.env.MIX_APP_ENV,
    pageUrl: to.fullPath,
    pageType: getPageTypeFromRouteName(to.name),
    hostname: window?.location?.hostname || '',
    ...getBasicUserData(),
  };
  dataLayerTrack({ eventName: gtmEvents.pageMetaData, data: gtmData });
};

const setReferralLinkToCookies = (code) => {
  const { plan_name, has_team, is_team_admin } = store.state?.user;
  // changed from plan_name !== 'Annual' because referral program does not support Monthly and Continuity plan yet
  const isReferralAvailableForUser = plan_name !== 'Annual' && plan_name !== 'Monthly' && plan_name !== 'Continuity';
  if (isReferralAvailableForUser && !has_team) {
    const codeCookieValue = {
      code,
      created_at: getUTCDateTime(),
    };
    Cookies.set(COOKIES.NAMES.REFERRAL_CODE, codeCookieValue, {
      expires: 14,
    });
  }
  if (has_team) {
    if (is_team_admin) {
      router.replace('/account/team/manage-members/');
    } else {
      router.replace('/account/details/');
    }
  } else {
    router.replace('/pricing/');
  }
};

if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-unused-expressions
  window.analytics?.ready(() => {
    // Reset the stored user data in segment in case user gets kicked out
    // without actual logout
    const userId = window.analytics.user().id();
    if (userId && !store.getters.isLoggedIn) {
      reset();
    }
  });
}

router.beforeEach((to, from, next) => {
  const { params, path } = to;
  const isReferral = path.startsWith('/refer-to-friend');
  if (isReferral) {
    setReferralLinkToCookies(params?.code);
  }

  touchUserSession();
  setRouteSuperProperties(to);
  store.dispatch('search/checkSearchId', to);
  Vue.nextTick(() => {
    // When /reactivate page reidrects to payment page,
    // pageview event will be added reactivation_page = 'checkout page' property
    const reactivationPage = to.name === 'pricing-plan' && to.query.reactivation_page ? to.query.reactivation_page : null;

    const additionalPageViewArgs = {
      ...(reactivationPage && { reactivation_page: 'checkout page' }),
    };

    eventTracker(analyticsEvents.pageView, { previous_page: from.path, ...additionalPageViewArgs });
  });
  next();
});

router.afterEach((to) => {
  setTimeout(() => {
    if (typeof window !== 'undefined') {
      trackPageMetaDataEvent(to);
    }
  }, 0);
});

export default new Vue({
  router,
  store,
  render: h => h(App),
});
